import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { differenceInMinutes, isPast, parseISO } from 'date-fns';

export interface CallNotification {
  id: string;
  title: string;
  scheduled_for: string;
  call_purpose: string | null;
  to_e164: string;
  contact_id: string;
  lead_id: string | null;
  reminder_time: string | null;
}

export function useCallNotificationsState() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<CallNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = async () => {
    if (!user) return;

    try {
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!appUser) return;

      // Get scheduled calls for this user that haven't happened yet
      const { data: calls, error } = await supabase
        .from('calls')
        .select('id, title, scheduled_for, reminder_time, to_e164, contact_id, lead_id, call_purpose')
        .eq('handled_by_app_user_id', appUser.id)
        .eq('status', 'scheduled')
        .eq('channel', 'voice')
        .not('scheduled_for', 'is', null)
        .gte('scheduled_for', new Date().toISOString())
        .order('scheduled_for', { ascending: true });

      if (error) {
        console.error('Error fetching scheduled calls:', error);
        return;
      }

      const now = new Date();
      const relevantCalls: CallNotification[] = [];

      calls?.forEach((call) => {
        const scheduledTime = parseISO(call.scheduled_for);
        const minutesUntilCall = differenceInMinutes(scheduledTime, now);

        // Default notification time is 15 minutes before
        let notificationMinutes = 15;
        
        if (call.reminder_time) {
          try {
            const reminderTime = parseISO(call.reminder_time);
            notificationMinutes = differenceInMinutes(scheduledTime, reminderTime);
          } catch {
            notificationMinutes = 15;
          }
        }

        // Show in notifications if within notification window and not past
        if (minutesUntilCall <= notificationMinutes && minutesUntilCall > 0 && !isPast(scheduledTime)) {
          relevantCalls.push(call as CallNotification);
        }
      });

      setNotifications(relevantCalls);
      setUnreadCount(relevantCalls.length);
    } catch (error) {
      console.error('Error loading call notifications:', error);
    }
  };

  const markAsRead = (callId: string) => {
    // When user clicks on notification, we can remove it from the list
    setNotifications(prev => prev.filter(n => n.id !== callId));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const removeNotification = (callId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== callId));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  useEffect(() => {
    if (!user) return;

    // Load notifications immediately
    loadNotifications();

    // Refresh every minute
    const interval = setInterval(loadNotifications, 60000);

    // Set up real-time subscription for changes
    const channel = supabase
      .channel('call-notifications-topbar')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calls',
        },
        (payload) => {
          console.log('Call change detected, refreshing notifications...');
          loadNotifications();
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [user]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    removeNotification,
    refresh: loadNotifications,
  };
}
